/**
 * DashboardPage - Teacher dashboard with metrics and recent sessions
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { sessionsService } from '@/services/sessions';
import { classesService } from '@/services/classes';
import { Session, DashboardMetrics, SessionStatus } from '@/types/session';
import { ClassWithMetrics, CreateClassRequest, UpdateClassRequest } from '@/types/class';
import { UncategorizedFolder } from '@/components/UncategorizedFolder';
import { CreateClassModal } from '@/components/CreateClassModal';

export function DashboardPage() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [recentSessions, setRecentSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<ClassWithMetrics[]>([]);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingClass, setEditingClass] = useState<ClassWithMetrics | undefined>(undefined);

  useEffect(() => {
    loadDashboardData();
  }, []);

  async function loadDashboardData() {
    try {
      setLoading(true);
      setError(null);

      // Load sessions and classes in parallel
      const [sessionsResponse, classesResponse] = await Promise.all([
        sessionsService.listSessions(),
        classesService.listClasses(),
      ]);

      setMetrics(sessionsResponse.metrics);
      setRecentSessions(sessionsResponse.sessions.slice(0, 5));
      setClasses(classesResponse.slice(0, 6)); // Show up to 6 classes

      // Count uncategorized sessions
      const uncategorized = sessionsResponse.sessions.filter((s) => !s.classId).length;
      setUncategorizedCount(uncategorized);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateClass(data: CreateClassRequest | UpdateClassRequest) {
    // For create, we need className to be present
    if ('className' in data && data.className) {
      await classesService.createClass(data as CreateClassRequest);
      await loadDashboardData(); // Refresh data
    }
  }

  async function handleUpdateClass(data: CreateClassRequest | UpdateClassRequest) {
    if (editingClass) {
      await classesService.updateClass(editingClass.classId, data);
      await loadDashboardData(); // Refresh data
    }
  }

  function handleEditClass(classItem: ClassWithMetrics) {
    setEditingClass(classItem);
    setIsCreateModalOpen(true);
  }

  function handleCloseModal() {
    setIsCreateModalOpen(false);
    setEditingClass(undefined);
  }

  function getStatusBadgeClass(status: SessionStatus): string {
    switch (status) {
      case SessionStatus.WAITING:
        return 'px-2 py-1 rounded text-xs font-medium bg-[#DD6832]/10 text-[#DD6832]';
      case SessionStatus.LIVE:
        return 'px-2 py-1 rounded text-xs font-medium bg-[#367A25]/10 text-[#367A25]';
      case SessionStatus.COMPLETE:
        return 'px-2 py-1 rounded text-xs font-medium bg-[#E2E5EC] text-[#12356B]';
      default:
        return 'px-2 py-1 rounded text-xs font-medium bg-[#E2E5EC] text-[#12356B]';
    }
  }

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#12356B] mb-4" />
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-800 mb-4">
            <strong>Error:</strong> {error}
          </div>
          <button onClick={loadDashboardData} className="px-6 py-2 bg-[#367A25] text-white rounded-lg hover:bg-[#2d6820] transition-colors">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-[#12356B]">Teacher Dashboard</h1>
          <button onClick={() => navigate('/login/teacher/new')} className="px-6 py-2 bg-[#367A25] text-white rounded-lg hover:bg-[#2d6820] transition-colors shadow-sm">
            Create Session
          </button>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white px-5 py-4 rounded-xl shadow-sm border border-[#CED4DE]">
            <h3 className="text-[11px] font-semibold text-slate-600 uppercase tracking-[0.14em] mb-1">Total Sessions</h3>
            <p className="text-3xl font-bold text-[#12356B] leading-tight">{metrics?.totalSessions || 0}</p>
            <p className="text-sm text-slate-500">All time</p>
          </div>

          <div className="bg-white px-5 py-4 rounded-xl shadow-sm border border-[#CED4DE]">
            <h3 className="text-[11px] font-semibold text-slate-600 uppercase tracking-[0.14em] mb-1">Active Sessions</h3>
            <p className="text-3xl font-bold text-[#DD6832] leading-tight">{metrics?.activeSessions || 0}</p>
            <p className="text-sm text-slate-500">Waiting + Live</p>
          </div>

          <div className="bg-white px-5 py-4 rounded-xl shadow-sm border border-[#CED4DE]">
            <h3 className="text-[11px] font-semibold text-slate-600 uppercase tracking-[0.14em] mb-1">Completed</h3>
            <p className="text-3xl font-bold text-[#367A25] leading-tight">{metrics?.completedSessions || 0}</p>
            <p className="text-sm text-slate-500">Total completed</p>
          </div>
        </div>

        {/* My Classes Section */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-[#12356B]">My Classes</h2>
            {classes.length > 6 && (
              <Link to="/login/teacher/classes" className="text-[#12356B] hover:text-[#0d2850] font-medium text-sm">
                View All →
              </Link>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Uncategorized folder */}
            <UncategorizedFolder
              sessionCount={uncategorizedCount}
              onClick={() => navigate('/login/teacher/sessions?classId=uncategorized')}
            />
            {/* Class cards */}
            {classes.map((classItem) => (
              <div key={classItem.classId} className="h-full">
                {/* Using inline card instead of ClassList for more control */}
                <div
                  onClick={() => navigate(`/login/teacher/classes/${classItem.classId}`)}
                  className="relative border border-[#CED4DE] rounded-lg p-4 hover:shadow-md hover:border-[#12356B]/40 transition-all cursor-pointer bg-white h-full flex flex-col"
                  style={
                    classItem.color
                      ? { borderLeftColor: classItem.color, borderLeftWidth: '4px' }
                      : undefined
                  }
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClass(classItem);
                    }}
                    className="absolute top-2.5 right-2.5 text-slate-400 hover:text-[#12356B] transition-colors"
                    aria-label="Edit class"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                  <h3 className="text-base font-semibold mb-1.5 pr-8 truncate">{classItem.className}</h3>
                  {classItem.description && (
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{classItem.description}</p>
                  )}
                  <div className="flex items-center justify-between mt-auto">
                    <span className="px-2 py-1 bg-[#E2E5EC] text-[#12356B] rounded text-xs font-medium">
                      {classItem.sessionCount} {classItem.sessionCount === 1 ? 'session' : 'sessions'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {/* Create new class card */}
            <div
              onClick={() => setIsCreateModalOpen(true)}
              className="border-2 border-dashed border-[#CED4DE] rounded-lg p-4 hover:border-[#12356B] hover:bg-[#E2E5EC]/60 transition-all cursor-pointer flex flex-col items-center justify-center h-full bg-white"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-10 w-10 text-slate-400 mb-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              <span className="text-sm font-medium text-slate-600">Create New Class</span>
            </div>
          </div>
        </div>

        {/* Recent Sessions */}
        <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm overflow-hidden">
          <div className="flex justify-between items-center px-6 py-4 border-b border-[#CED4DE]">
            <h2 className="text-xl font-bold text-[#12356B]">Recent Sessions</h2>
            <Link to="/login/teacher/sessions" className="text-[#12356B] hover:text-[#0d2850] font-medium text-sm">
              View All →
            </Link>
          </div>

          {recentSessions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600 text-lg mb-2">No sessions yet</p>
              <p className="text-slate-500 mb-4">Create your first session to get started!</p>
              <button onClick={() => navigate('/login/teacher/new')} className="px-6 py-2 bg-[#367A25] text-white rounded-lg hover:bg-[#2d6820] transition-colors">
                Create Session
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-[#E2E5EC]/60 border-b border-[#CED4DE]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E5EC]">
                {recentSessions.map((session) => (
                  <tr key={session.sessionId} className="hover:bg-[#E2E5EC]/35">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-[#E2E5EC] text-[#12356B] rounded text-sm font-semibold tracking-[0.08em]">
                        {session.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-black">
                      {session.sessionName || <span className="text-slate-400 italic">Unnamed</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadgeClass(session.status)}>
                        {session.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{formatDate(session.createdAt)}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/login/teacher/sessions/${session.sessionId}`)}
                          className="px-3 py-1.5 bg-[#367A25] text-white rounded hover:bg-[#2d6820] transition-colors text-sm font-medium shadow-sm"
                        >
                          View
                        </button>
                        {session.status === SessionStatus.COMPLETE && (
                          <button
                            onClick={() => navigate(`/login/teacher/sessions/${session.sessionId}/results`)}
                            className="px-3 py-1.5 bg-[#DD6832] text-white rounded hover:bg-[#c4562a] transition-colors text-sm font-medium shadow-sm"
                          >
                            Results
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create/Edit Class Modal */}
      <CreateClassModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        onSubmit={editingClass ? handleUpdateClass : handleCreateClass}
        initialData={editingClass}
      />
    </div>
  );
}
